import { useUnit } from 'effector-react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { placesFor, recordFillFx } from '../../entities/position';
import { fromLocalInput, toLocalInput } from '../../shared/lib/dates';
import { parseDecimal } from '../../shared/lib/decimal';
import { formatMoney, formatShares } from '../../shared/lib/format';
import { useLocale } from '../../shared/lib/i18n/use-locale';
import { Button, Dialog, DialogContent, Field, Input, Select } from '../../shared/ui';
import {
  $errors,
  $preview,
  $request,
  fillDialogClosed,
  fillPreview,
  type FillRequest,
} from './model';

export function RecordFillDialog() {
  const [request, close] = useUnit([$request, fillDialogClosed]);
  return (
    <Dialog
      open={request !== null}
      onOpenChange={(open) => {
        if (!open) close();
      }}
    >
      {request && (
        <RecordFillForm
          key={`${String(request.position.id)}:${String(request.level)}`}
          request={request}
        />
      )}
    </Dialog>
  );
}

function decimalInput(value: string, locale: 'en' | 'bg'): string {
  return locale === 'bg' ? value.replace('.', ',') : value;
}

function RecordFillForm({ request }: { request: FillRequest }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const { position } = request;
  const places = placesFor(position);
  const open = position.levels.filter(
    (l) => l.n > 0 && (l.status === 'pending' || l.status === 'triggered'),
  );
  const [level, setLevel] = useState(String(request.level));
  const planned = position.levels.find((l) => String(l.n) === level);
  const [price, setPrice] = useState(decimalInput(planned?.planned_price ?? '', locale));
  const [shares, setShares] = useState(decimalInput(planned?.planned_shares ?? '', locale));
  const [executedAt, setExecutedAt] = useState(toLocalInput(new Date()));
  const [attempted, setAttempted] = useState(false);
  const [preview, serverErrors, pending, close, submit, requestPreview] = useUnit([
    $preview,
    $errors,
    recordFillFx.pending,
    fillDialogClosed,
    recordFillFx,
    fillPreview.run,
  ]);

  const parsedPrice = parseDecimal(price, locale);
  const parsedShares = parseDecimal(shares, locale);
  const parsedTime = fromLocalInput(executedAt);

  useEffect(() => {
    if (
      parsedPrice === null ||
      parsedShares === null ||
      Number(parsedPrice) <= 0 ||
      Number(parsedShares) <= 0
    ) {
      return undefined;
    }
    const timer = window.setTimeout(() => {
      void requestPreview({
        id: position.id,
        body: { price: parsedPrice, shares: parsedShares },
      }).catch(() => undefined);
    }, 150);
    return () => {
      window.clearTimeout(timer);
    };
  }, [parsedPrice, parsedShares, position.id, requestPreview]);

  const errorFor = (field: string, raw: string, parsed: string | null): string | undefined =>
    serverErrors[field] ??
    (raw.trim() !== '' && parsed === null
      ? t('form.notANumber')
      : attempted && parsed === null
        ? t('form.required')
        : undefined);

  function onSubmit(): void {
    setAttempted(true);
    if (parsedPrice === null || parsedShares === null || parsedTime === null) return;
    void submit({
      id: position.id,
      body: {
        level_n: Number(level),
        price: parsedPrice,
        shares: parsedShares,
        executed_at: parsedTime,
      },
    }).catch(() => undefined);
  }

  return (
    <DialogContent
      title={t('recordFill.title', { symbol: position.instrument.display_symbol })}
      description={t('recordFill.description')}
      closeLabel={t('common.close')}
      onSubmit={onSubmit}
      footer={
        <>
          <Button onClick={close}>{t('common.cancel')}</Button>
          <Button type="submit" variant="primary" disabled={pending}>
            {t('recordFill.submit')}
          </Button>
        </>
      }
    >
      <div className="space-y-2">
        <Field label={t('recordFill.level')} error={serverErrors.level_n}>
          {({ id, describedBy }) => (
            <Select
              id={id}
              aria-describedby={describedBy}
              value={level}
              options={open.map((l) => ({
                value: String(l.n),
                text: t('ladder.levelN', { n: l.n }),
                label: (
                  <span className="flex items-center gap-2">
                    {t('ladder.levelN', { n: l.n })}
                    <span className="tabular text-text-3">
                      {formatMoney(l.planned_price, locale, { places })} ·{' '}
                      {formatShares(l.planned_shares, locale)}
                    </span>
                  </span>
                ),
              }))}
              onValueChange={(value) => {
                setLevel(value);
                const next = position.levels.find((l) => String(l.n) === value);
                if (next) {
                  setPrice(decimalInput(next.planned_price, locale));
                  setShares(decimalInput(next.planned_shares, locale));
                }
              }}
            />
          )}
        </Field>
        <Field label={t('recordFill.price')} error={errorFor('price', price, parsedPrice)}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              inputMode="decimal"
              numeric
              unit="€"
              autoComplete="off"
              value={price}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              onChange={(event) => {
                setPrice(event.target.value);
              }}
            />
          )}
        </Field>
        <Field label={t('recordFill.shares')} error={errorFor('shares', shares, parsedShares)}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              inputMode="decimal"
              numeric
              autoComplete="off"
              value={shares}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              onChange={(event) => {
                setShares(event.target.value);
              }}
            />
          )}
        </Field>
        <Field label={t('recordFill.executedAt')} error={serverErrors.executed_at}>
          {({ id, describedBy, invalid }) => (
            <Input
              id={id}
              type="datetime-local"
              value={executedAt}
              aria-describedby={describedBy}
              aria-invalid={invalid || undefined}
              onChange={(event) => {
                setExecutedAt(event.target.value);
              }}
            />
          )}
        </Field>
      </div>
      {preview && (
        <dl className="mt-4 grid grid-cols-3 gap-3 rounded-lg border border-border-subtle bg-bg-1 px-3 py-2.5 text-xs">
          <div>
            <dt className="text-text-3">{t('position.shares')}</dt>
            <dd className="tabular mt-0.5 text-sm font-medium">
              {formatShares(preview.shares, locale)}
            </dd>
          </div>
          <div>
            <dt className="text-text-3">{t('position.newAverage')}</dt>
            <dd className="tabular mt-0.5 text-sm font-medium">
              {formatMoney(preview.average, locale, { places })}
            </dd>
          </div>
          <div>
            <dt className="text-text-3">{t('position.newTarget')}</dt>
            <dd className="tabular mt-0.5 text-sm font-medium">
              {formatMoney(preview.target_price, locale, { places })}
            </dd>
          </div>
        </dl>
      )}
    </DialogContent>
  );
}
